import React, { useState } from 'react';
import { Coins, Loader2, Search } from 'lucide-react';
import { Skeleton } from 'App/shadcn/ui/skeleton';
import T from 'Components/Helpers/translate';
import { formatCredits } from 'App/shadcn/pages/AIWorkspace/Admin/workspaceSettingsShared';
import { Button } from 'App/shadcn/ui/button';
import { cn } from 'App/shadcn/lib/utils';
import { isVerified, PROVIDER_NAMES, type LookupValue } from './lookupApi';

export const CARD = 'overflow-hidden rounded-[8px] border border-grey-4 bg-background';

export const PILL =
	'inline-flex w-fit items-center whitespace-nowrap rounded-full px-[10px] py-[3px] text-body-sm font-medium';

/** A link as the rest of the system draws one: the text colour, underlined (WEB-13771). */
export const LINK = 'text-foreground underline';

/** "linkedin.com/in/anna-lind" for https://www.linkedin.com/in/anna-lind/ - what a rep reads at a glance. */
const shortQuery = (query: string) =>
	query
		.trim()
		.replace(/^https?:\/\/(www\.)?/i, '')
		.replace(/\/$/, '');

/**
 * What the rep looked up, as a card under the panel's header (WEB-13771). Shown while it is being
 * looked up and when nothing was found; a found result's own card names it, so it is not repeated.
 */
export const QueryCard = ({ query, loading }: { query: string; loading: boolean }) => (
	<div className={cn(CARD, 'flex items-center gap-3 p-4')}>
		<span
			aria-hidden
			className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-grey-2 text-grey-11"
		>
			<Search size={16} />
		</span>
		<span className="min-w-0 flex-1">
			{loading ? (
				<span role="status" className="block text-body-sm text-grey-11">
					{T('prospectingTools.lookup.loading')}
				</span>
			) : null}
			<span className="block truncate text-body-md font-medium text-grey-13" title={query}>
				{shortQuery(query)}
			</span>
		</span>
		{loading ? <Loader2 size={16} aria-hidden className="shrink-0 animate-spin text-grey-10" /> : null}
	</div>
);

/** The shape of a result while it loads, so the panel does not sit on a line of text. */
export const ResultSkeleton = () => (
	<div aria-hidden className={cn(CARD, 'flex flex-col gap-4 p-5')}>
		<div className="flex items-center gap-3">
			<Skeleton className="h-12 w-12 rounded-full" />
			<div className="flex flex-1 flex-col gap-2">
				<Skeleton className="h-4 w-2/5" />
				<Skeleton className="h-3 w-3/5" />
			</div>
		</div>
		<Skeleton className="h-3 w-full" />
		<Skeleton className="h-3 w-4/5" />
		<Skeleton className="h-3 w-3/5" />
	</div>
);

/** Said before an action that spends AI credits, so a rep knows before clicking (WEB-13771). */
export const UsesCredits = () => (
	<p className="m-0 flex items-center gap-1 text-body-sm text-grey-10">
		<Coins size={12} aria-hidden />
		{T('prospectingTools.lookup.usesCredits')}
	</p>
);

/** What a result cost, shown at the top of it rather than after it. */
export const CreditsPill = ({ credits }: { credits: number }) => (
	<span className={cn(PILL, 'gap-1 bg-grey-2 text-body-sm text-grey-11')}>
		<Coins size={11} aria-hidden />
		{T('prospectingTools.lookup.aiCredits', { count: formatCredits(credits) })}
	</span>
);

export const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
		} catch (err) {
			// A browser that refuses clipboard access leaves the value on screen to select by hand.
			console.error('[Lookup] copy failed', err);
		}
	};
	return (
		<Button type="button" variant="ghost" size="sm" onClick={() => void copy()}>
			{copied ? T('default.copiedToClipboard') : T('default.copy')}
		</Button>
	);
};

/** Where a value came from: pasted, a provider, or a provider at a waterfall step. */
export const sourceOf = (value: LookupValue) => {
	if (value.provider === 'input') return T('prospectingTools.lookup.source.input');
	const provider = PROVIDER_NAMES[value.provider];
	return value.step ? T('prospectingTools.lookup.source.step', { provider, step: value.step }) : provider;
};

type RowProps = {
	label: string;
	value: string;
	/** The waterfall's answer, for its source and verdict; absent for a value with neither. */
	found?: LookupValue;
	href?: string;
};

/** One label / value / provenance row of a result card's definition list. */
export const ValueRow = ({ label, value, found, href }: RowProps) => (
	<div className="flex flex-col gap-1 border-b border-grey-2 px-5 py-3 sm:grid sm:grid-cols-[minmax(110px,160px)_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
		<dt className="text-body-md text-grey-11">{label}</dt>
		<dd className="m-0 min-w-0 break-all text-body-md text-grey-13">
			{href ? (
				<a href={href} target="_blank" rel="noopener noreferrer" className={LINK}>
					{value}
				</a>
			) : (
				value
			)}
		</dd>
		<dd className="m-0 flex flex-wrap items-center gap-2 sm:justify-end">
			{found && isVerified(found) ? (
				<span className={cn(PILL, 'bg-success-light text-body-sm text-success-dark')}>
					{T('prospectingTools.lookup.verified')}
				</span>
			) : null}
			{found ? <span className="text-body-sm text-grey-11">{sourceOf(found)}</span> : null}
			<CopyButton value={value} />
		</dd>
	</div>
);
